import React from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { State, GameMode } from '../../types';
import { getMapConfig, usesWorldMap } from '../../config/mapConfig';
import './Game.css';

// Map FIPS codes to state abbreviations (for US map)
const FIPS_TO_STATE: { [key: string]: string } = {
  '01': 'AL', '02': 'AK', '04': 'AZ', '05': 'AR', '06': 'CA', '08': 'CO', '09': 'CT', '10': 'DE',
  '12': 'FL', '13': 'GA', '15': 'HI', '16': 'ID', '17': 'IL', '18': 'IN', '19': 'IA', '20': 'KS',
  '21': 'KY', '22': 'LA', '23': 'ME', '24': 'MD', '25': 'MA', '26': 'MI', '27': 'MN', '28': 'MS',
  '29': 'MO', '30': 'MT', '31': 'NE', '32': 'NV', '33': 'NH', '34': 'NJ', '35': 'NM', '36': 'NY',
  '37': 'NC', '38': 'ND', '39': 'OH', '40': 'OK', '41': 'OR', '42': 'PA', '44': 'RI', '45': 'SC',
  '46': 'SD', '47': 'TN', '48': 'TX', '49': 'UT', '50': 'VT', '51': 'VA', '53': 'WA', '54': 'WV',
  '55': 'WI', '56': 'WY'
};

// Map numeric ISO codes (from world-atlas) to alpha-2 codes
// Generated from world-countries package - 194 countries
const ISO_NUMERIC_TO_ISO2: { [key: string]: string } = {
  '004': 'AF', '008': 'AL', '012': 'DZ', '020': 'AD', '024': 'AO', '028': 'AG', '031': 'AZ',
  '032': 'AR', '036': 'AU', '040': 'AT', '044': 'BS', '048': 'BH', '050': 'BD', '051': 'AM',
  '052': 'BB', '056': 'BE', '064': 'BT', '068': 'BO', '070': 'BA', '072': 'BW', '076': 'BR',
  '084': 'BZ', '090': 'SB', '096': 'BN', '100': 'BG', '104': 'MM', '108': 'BI', '112': 'BY',
  '116': 'KH', '120': 'CM', '124': 'CA', '132': 'CV', '140': 'CF', '144': 'LK', '148': 'TD',
  '152': 'CL', '156': 'CN', '158': 'TW', '170': 'CO', '174': 'KM', '178': 'CG', '180': 'CD', '188': 'CR',
  '191': 'HR', '192': 'CU', '196': 'CY', '203': 'CZ', '204': 'BJ', '208': 'DK', '212': 'DM',
  '214': 'DO', '218': 'EC', '222': 'SV', '226': 'GQ', '231': 'ET', '232': 'ER', '233': 'EE',
  '242': 'FJ', '246': 'FI', '250': 'FR', '262': 'DJ', '266': 'GA', '268': 'GE', '270': 'GM',
  '276': 'DE', '288': 'GH', '296': 'KI', '300': 'GR', '308': 'GD', '320': 'GT', '324': 'GN',
  '328': 'GY', '332': 'HT', '336': 'VA', '340': 'HN', '348': 'HU', '352': 'IS', '356': 'IN',
  '360': 'ID', '364': 'IR', '368': 'IQ', '372': 'IE', '376': 'IL', '380': 'IT', '384': 'CI',
  '388': 'JM', '392': 'JP', '398': 'KZ', '400': 'JO', '404': 'KE', '408': 'KP', '410': 'KR',
  '414': 'KW', '417': 'KG', '418': 'LA', '422': 'LB', '426': 'LS', '428': 'LV', '430': 'LR',
  '434': 'LY', '438': 'LI', '440': 'LT', '442': 'LU', '450': 'MG', '454': 'MW', '458': 'MY',
  '462': 'MV', '466': 'ML', '470': 'MT', '478': 'MR', '480': 'MU', '484': 'MX', '492': 'MC',
  '496': 'MN', '498': 'MD', '499': 'ME', '504': 'MA', '508': 'MZ', '512': 'OM', '516': 'NA',
  '520': 'NR', '524': 'NP', '528': 'NL', '548': 'VU', '554': 'NZ', '558': 'NI', '562': 'NE',
  '566': 'NG', '578': 'NO', '583': 'FM', '584': 'MH', '585': 'PW', '586': 'PK', '591': 'PA',
  '598': 'PG', '600': 'PY', '604': 'PE', '608': 'PH', '616': 'PL', '620': 'PT', '624': 'GW',
  '626': 'TL', '634': 'QA', '642': 'RO', '643': 'RU', '646': 'RW', '659': 'KN', '662': 'LC',
  '670': 'VC', '674': 'SM', '678': 'ST', '682': 'SA', '686': 'SN', '688': 'RS', '690': 'SC',
  '694': 'SL', '702': 'SG', '703': 'SK', '704': 'VN', '705': 'SI', '706': 'SO', '710': 'ZA',
  '716': 'ZW', '724': 'ES', '728': 'SS', '729': 'SD', '740': 'SR', '748': 'SZ', '752': 'SE',
  '756': 'CH', '760': 'SY', '762': 'TJ', '764': 'TH', '768': 'TG', '776': 'TO', '780': 'TT',
  '784': 'AE', '788': 'TN', '792': 'TR', '795': 'TM', '798': 'TV', '800': 'UG', '804': 'UA',
  '807': 'MK', '818': 'EG', '826': 'GB', '834': 'TZ', '840': 'US', '854': 'BF', '858': 'UY',
  '860': 'UZ', '862': 'VE', '882': 'WS', '887': 'YE', '894': 'ZM'
};

interface MapProps {
  onStateClick: (stateAbbr: string) => void;
  completedStates: Set<string>;
  incorrectStates?: Set<string>;
  incorrectState: string | null;
  learnMode?: boolean;
  allStates?: State[];
  gameMode: GameMode;
}

const Map: React.FC<MapProps> = ({
  onStateClick,
  completedStates,
  incorrectStates = new Set(),
  incorrectState,
  learnMode = false,
  allStates = [],
  gameMode
}) => {
  const mapConfig = getMapConfig(gameMode);
  const isWorldMap = usesWorldMap(gameMode);

  // Get entity by abbreviation
  const getEntity = (abbr: string): State | undefined => {
    return allStates.find(s => s.abbreviation === abbr);
  };

  const getEntityName = (abbr: string): string => {
    const entity = getEntity(abbr);
    return entity ? entity.name : '';
  };

  const getEntityCapital = (abbr: string): string => {
    const entity = getEntity(abbr);
    return entity ? entity.capital : '';
  };

  // Convert geography ID to abbreviation
  const getAbbreviationFromGeo = (geo: any): string => {
    if (isWorldMap) {
      // For world map, geo.id is numeric ISO code
      const numericCode = geo.id;
      const iso2 = ISO_NUMERIC_TO_ISO2[numericCode] || geo.properties?.ISO_A2 || numericCode;
      return iso2;
    } else {
      // For US map, geo.id is FIPS code
      return FIPS_TO_STATE[geo.id] || geo.id;
    }
  };

  const getFillColor = (abbr: string): string => {
    if (incorrectState === abbr) {
      return '#F44336'; // Red flash for wrong click
    }
    if (incorrectStates.has(abbr)) {
      return '#B71C1C'; // Darker red for auto-revealed (failed) states
    }
    if (completedStates.has(abbr)) {
      return '#81C784'; // Lighter green for correctly answered
    }
    return '#ADD8E6'; // Default light blue
  };

  const getOpacity = (abbr: string): number => {
    if (completedStates.has(abbr) && incorrectState !== abbr) {
      return 0.6; // Dimmed for completed states
    }
    return 1;
  };

  return (
    <div className="map-container" role="main" aria-label="Interactive map">
      <TransformWrapper
        initialScale={mapConfig.initialZoom || 1}
        minScale={mapConfig.minZoom || 0.5}
        maxScale={mapConfig.maxZoom || 4}
        centerOnInit={true}
        wheel={{ step: 0.1 }}
        panning={{ disabled: false }}
        doubleClick={{ disabled: false }}
      >
        {({ zoomIn, zoomOut, resetTransform }) => (
          <>
            {/* Zoom Controls */}
            <div className="map-zoom-controls">
              <button
                onClick={() => zoomIn()}
                className="zoom-btn"
                aria-label="Zoom in"
                title="Zoom in"
              >
                +
              </button>
              <button
                onClick={() => zoomOut()}
                className="zoom-btn"
                aria-label="Zoom out"
                title="Zoom out"
              >
                −
              </button>
              <button
                onClick={() => resetTransform()}
                className="zoom-btn"
                aria-label="Reset zoom"
                title="Reset view"
              >
                ⟲
              </button>
            </div>

            <TransformComponent
              wrapperStyle={{ width: '100%', height: '100%' }}
              contentStyle={{ width: '100%', height: '100%' }}
            >
              <ComposableMap
                projection={mapConfig.projection as any}
                projectionConfig={{
                  scale: mapConfig.scale,
                  center: mapConfig.center
                }}
                style={{ width: '100%', height: '100%' }}
              >
                <Geographies geography={mapConfig.geoUrl}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const abbr = getAbbreviationFromGeo(geo);
                      const entity = getEntity(abbr);

                      // For entities in our game data, make them interactive
                      const isInGame = !!entity;
                      const isCompleted = completedStates.has(abbr);
                      const isIncorrect = incorrectState === abbr;

                      // Render all geographies, but only make game entities interactive
                      return (
                        <g key={geo.rsmKey}>
                          <Geography
                            geography={geo}
                            fill={isInGame ? getFillColor(abbr) : '#8B9DC3'}
                            stroke="#FFF"
                            strokeWidth={0.5}
                            style={{
                              default: {
                                fill: isInGame ? getFillColor(abbr) : '#8B9DC3',
                                opacity: isInGame ? getOpacity(abbr) : 0.6,
                                outline: 'none',
                              },
                              hover: {
                                fill: isInGame ? (isCompleted ? getFillColor(abbr) : '#87CEEB') : '#8B9DC3',
                                opacity: isInGame ? (isCompleted ? 0.6 : 1) : 0.6,
                                outline: 'none',
                                cursor: isInGame && !isCompleted ? 'pointer' : 'default',
                              },
                              pressed: {
                                fill: isInGame ? getFillColor(abbr) : '#8B9DC3',
                                outline: 'none',
                              },
                            }}
                            onClick={() => {
                              if (isInGame && (!isCompleted || learnMode)) {
                                onStateClick(abbr);
                              }
                            }}
                            tabIndex={isInGame ? 0 : -1}
                            role={isInGame ? "button" : undefined}
                            aria-label={isInGame ? `${getEntityName(abbr) || abbr}${
                              isCompleted ? ' - completed' : ''
                            }` : undefined}
                            onKeyPress={(e) => {
                              if (isInGame && e.key === 'Enter' && (!isCompleted || learnMode)) {
                                onStateClick(abbr);
                              }
                            }}
                          />
                          {isInGame && (isCompleted || learnMode) && geo.properties?.centroid && (
                            <text
                              x={geo.properties.centroid[0]}
                              y={geo.properties.centroid[1]}
                              textAnchor="middle"
                              fill="white"
                              fontSize={isWorldMap ? 6 : 8}
                              fontWeight="bold"
                              pointerEvents="none"
                              style={{ userSelect: 'none' }}
                            >
                              <tspan x={geo.properties.centroid[0]} dy="-0.3em">
                                {getEntityName(abbr)}
                              </tspan>
                              {isCompleted && !learnMode && (
                                <tspan
                                  x={geo.properties.centroid[0]}
                                  dy="1.2em"
                                  fontSize={isWorldMap ? 4 : 6}
                                >
                                  {getEntityCapital(abbr)}
                                </tspan>
                              )}
                            </text>
                          )}
                        </g>
                      );
                    })
                  }
                </Geographies>
              </ComposableMap>
            </TransformComponent>

            {/* Zoom Instructions */}
            <div className="map-instructions">
              <small>💡 Pinch to zoom on mobile • Scroll to zoom on desktop</small>
            </div>
          </>
        )}
      </TransformWrapper>
    </div>
  );
};

export default Map;
